import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  BankAccount,
  Contractor,
  Contract,
  PlanfactOperation,
  Task,
  WorkType,
  getBankAccounts,
  getContractor,
  getContracts,
  getPlanfactOperations,
  getTasks,
  getWorkTypes,
  updateContractor,
  deleteContractor,
  type ContractNote,
} from '../services/api';
import { formatContractSumDisplay } from '../utils/table';
import { buildContractReceiptsTabData } from '../utils/contractReceiptsTab';
import { resolveContractorFinance, sortContractsByFamilyHierarchy } from '../utils/contractFamily';
import { useAuth } from '../context/AuthContext';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import PaymentDonut from '../components/PaymentDonut';
import ContractPaymentNotesChat from '../components/ContractPaymentNotesChat';
import '../styles/DataPage.css';
import '../styles/ContractorDetailPage.css';
import './TaskDetailPage.css';
import './ContractDetailPage.css';

type TabId = 'requisites' | 'contacts' | 'hierarchy' | 'receipts';

type ContractorTypeCode = 'ФЛ' | 'ЮЛ';

function contractorTypeLabel(type: string): string {
  if (type === 'ЮЛ') return 'Юридическое лицо';
  if (type === 'ФЛ') return 'Физическое лицо';
  return type;
}

/** Черновик реквизитов и контактов для режима редактирования карточки */
interface ContractorCardDraft {
  contractor_type: ContractorTypeCode;
  inn: string;
  address: string;
  passport_data: string;
  phone: string;
  email: string;
  position_relation: string;
  contact_full_name: string;
  additional_contacts: string;
}

function contractorToDraft(c: Contractor): ContractorCardDraft {
  return {
    contractor_type: (c.contractor_type === 'ФЛ' ? 'ФЛ' : 'ЮЛ') as ContractorTypeCode,
    inn: c.inn ?? '',
    address: c.address ?? '',
    passport_data: c.passport_data ?? '',
    phone: c.phone ?? '',
    email: c.email ?? '',
    position_relation: c.position_relation ?? '',
    contact_full_name: c.contact_full_name ?? '',
    additional_contacts: c.additional_contacts ?? '',
  };
}

function dash(value: string | undefined | null): string {
  if (value == null || String(value).trim() === '') return '—';
  return String(value);
}

function taskDetailPath(task: Pick<Task, 'id' | 'contracts_task_id'>): string {
  return `/tasks/${task.id}?contracts_task_id=${task.contracts_task_id}`;
}

const ContractorDetailPage: React.FC = () => {
  const { id: idParam } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { can, hasDepartment, hasRole } = useAuth();
  const canOpenContractorCard =
    hasDepartment('ALL_COMP') || hasRole('ОП_Менеджер', 'ОУП_Рук. проекта');
  const canContractors = can('CONTRACTORS', 'VIEW');
  const canEditContractor = can('CONTRACTORS', 'EDIT');
  const canDeleteContractor = can('CONTRACTORS', 'DELETE');
  const canContracts = can('CONTRACTS', 'VIEW');
  const canEditContract = can('CONTRACTS', 'EDIT');
  const canTasks = can('TASKS', 'VIEW');
  const canSum = can('CONTRACT_SUM', 'VIEW');
  const canIncome = can('INCOME', 'VIEW');
  const canReceipts = canSum && canIncome;

  const contractorId = idParam ? parseInt(idParam, 10) : NaN;

  const [contractor, setContractor] = useState<Contractor | null>(null);
  const [contracts, setContracts] = useState<Contract[]>([]);
  const [tasks, setTasks] = useState<Task[]>([]);
  const [workTypes, setWorkTypes] = useState<WorkType[]>([]);
  const [planfactOperations, setPlanfactOperations] = useState<PlanfactOperation[]>([]);
  const [bankAccounts, setBankAccounts] = useState<BankAccount[]>([]);
  const [hierarchyExpandedContractIds, setHierarchyExpandedContractIds] = useState<Set<number>>(
    () => new Set(),
  );
  const [activeTab, setActiveTab] = useState<TabId>('requisites');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useDocumentTitle(contractor?.name || 'Контрагент');

  const [headerName, setHeaderName] = useState('');
  const [nameSaving, setNameSaving] = useState(false);
  const [nameError, setNameError] = useState('');

  const [cardEditing, setCardEditing] = useState(false);
  const [cardDraft, setCardDraft] = useState<ContractorCardDraft | null>(null);
  const [cardSaving, setCardSaving] = useState(false);
  const [cardError, setCardError] = useState('');

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [deleteDialogError, setDeleteDialogError] = useState('');
  const [paymentNotesOpen, setPaymentNotesOpen] = useState(false);

  const toggleHierarchyContract = (contractId: number): void => {
    setHierarchyExpandedContractIds((prev) => {
      const next = new Set(prev);
      if (next.has(contractId)) next.delete(contractId);
      else next.add(contractId);
      return next;
    });
  };

  const load = useCallback(async (): Promise<void> => {
    if (!canOpenContractorCard || !canContractors || Number.isNaN(contractorId)) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError('');
    try {
      const c = await getContractor(contractorId);
      setContractor(c);

      let contractRows: Contract[] = [];
      if (canContracts) {
        const all = await getContracts();
        contractRows = all.filter((x) => Number(x.contractor_id) === contractorId);
        setContracts(contractRows);
      } else {
        setContracts([]);
      }

      const [operations, accounts, types] = await Promise.all([
        canReceipts ? getPlanfactOperations().catch(() => [] as PlanfactOperation[]) : Promise.resolve([] as PlanfactOperation[]),
        getBankAccounts().catch(() => [] as BankAccount[]),
        canTasks ? getWorkTypes().catch(() => [] as WorkType[]) : Promise.resolve([] as WorkType[]),
      ]);
      setPlanfactOperations(operations);
      setBankAccounts(accounts);
      setWorkTypes(types);

      if (canTasks && contractRows.length > 0) {
        const allT = await getTasks();
        const contractIds = new Set(contractRows.map((x) => x.id));
        const taskRows = allT.filter((t) => contractIds.has(t.contract_id));
        setTasks(taskRows);
      } else if (canTasks) {
        setTasks([]);
      } else {
        setTasks([]);
      }
    } catch (e) {
      setError('Не удалось загрузить данные контрагента');
      setContractor(null);
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [canOpenContractorCard, canContractors, canContracts, canTasks, canReceipts, contractorId]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    if (activeTab === 'receipts' && !canReceipts) {
      setActiveTab('requisites');
    }
  }, [activeTab, canReceipts]);

  useEffect(() => {
    setCardEditing(false);
    setCardDraft(null);
    setCardError('');
    setPaymentNotesOpen(false);
  }, [contractorId]);

  useEffect(() => {
    if (contractor) {
      setHeaderName(contractor.name);
    }
  }, [contractor]);

  useEffect(() => {
    if (contracts.length > 0) {
      setHierarchyExpandedContractIds(new Set(contracts.map((ct) => ct.id)));
    }
  }, [contracts]);

  const setActiveTabSafe = useCallback((t: TabId) => {
    if (t === 'receipts' && !canReceipts) return;
    setActiveTab(t);
  }, [canReceipts]);

  const handleNameBlur = useCallback(async (): Promise<void> => {
    if (!canEditContractor || !contractor) return;
    const trimmed = headerName.trim();
    if (trimmed === contractor.name) return;
    if (!trimmed) {
      setHeaderName(contractor.name);
      return;
    }
    setNameSaving(true);
    setNameError('');
    try {
      const updated = await updateContractor(contractor.id, { name: trimmed });
      setContractor(updated);
      setHeaderName(updated.name);
    } catch (e) {
      setHeaderName(contractor.name);
      setNameError(e instanceof Error ? e.message : 'Не удалось сохранить название');
    } finally {
      setNameSaving(false);
    }
  }, [canEditContractor, contractor, headerName]);

  const handleStartCardEdit = useCallback((): void => {
    if (!contractor || !canEditContractor) return;
    setCardDraft(contractorToDraft(contractor));
    setCardEditing(true);
    setCardError('');
    setActiveTab((t) => (t === 'hierarchy' || t === 'receipts' ? 'requisites' : t));
  }, [contractor, canEditContractor]);

  const handleCancelCardEdit = useCallback((): void => {
    setCardEditing(false);
    setCardDraft(null);
    setCardError('');
  }, []);

  const handleSaveCardEdit = useCallback(async (): Promise<void> => {
    if (!contractor || cardDraft == null) return;
    const innTrim = cardDraft.inn.trim();
    if (cardDraft.contractor_type === 'ЮЛ' && !innTrim) {
      setCardError('Укажите ИНН для юридического лица');
      return;
    }
    setCardSaving(true);
    setCardError('');
    try {
      const updated = await updateContractor(contractor.id, {
        contractor_type: cardDraft.contractor_type,
        inn: innTrim,
        address: cardDraft.address,
        passport_data: cardDraft.passport_data,
        phone: cardDraft.phone,
        email: cardDraft.email,
        position_relation: cardDraft.position_relation,
        contact_full_name: cardDraft.contact_full_name,
        additional_contacts: cardDraft.additional_contacts,
      });
      setContractor(updated);
      setCardEditing(false);
      setCardDraft(null);
    } catch (e) {
      setCardError(e instanceof Error ? e.message : 'Не удалось сохранить');
    } finally {
      setCardSaving(false);
    }
  }, [contractor, cardDraft]);

  const handleDeleteClick = useCallback((): void => {
    setDeleteDialogError('');
    setDeleteDialogOpen(true);
  }, []);

  const handleDeleteDialogClose = useCallback((): void => {
    if (deleteBusy) return;
    setDeleteDialogOpen(false);
    setDeleteDialogError('');
  }, [deleteBusy]);

  const handleDeleteConfirm = useCallback(async (): Promise<void> => {
    if (!contractor || !canDeleteContractor) return;
    setDeleteBusy(true);
    setDeleteDialogError('');
    try {
      await deleteContractor(contractor.id);
      setDeleteDialogOpen(false);
      navigate('/contractors');
    } catch (e) {
      setDeleteDialogError(e instanceof Error ? e.message : 'Не удалось удалить контрагента');
    } finally {
      setDeleteBusy(false);
    }
  }, [contractor, canDeleteContractor, navigate]);

  const handleDeleteDialogBackdropMouseDown = useCallback(
    (e: React.MouseEvent<HTMLDivElement>): void => {
      if (e.target === e.currentTarget) {
        handleDeleteDialogClose();
      }
    },
    [handleDeleteDialogClose],
  );

  const tasksByContractId = useMemo(() => {
    const map = new Map<number, Task[]>();
    for (const t of tasks) {
      const list = map.get(t.contract_id) ?? [];
      list.push(t);
      map.set(t.contract_id, list);
    }
    for (const [, list] of map) {
      list.sort((a, b) => a.task_id.localeCompare(b.task_id, 'ru'));
    }
    return map;
  }, [tasks]);

  const sortedContracts = useMemo(
    () => sortContractsByFamilyHierarchy(contracts),
    [contracts],
  );

  const level1Contracts = useMemo(
    () => sortedContracts.filter((ct) => ct.hierarchy_level !== 2),
    [sortedContracts],
  );

  const level1ContractIds = useMemo(() => level1Contracts.map((ct) => ct.id), [level1Contracts]);

  const workTypeLabel = useCallback(
    (id: number): string => {
      const wt = workTypes.find((item) => item.id === id);
      return wt?.name ?? '—';
    },
    [workTypes],
  );

  const contractorFinance = useMemo(
    () =>
      resolveContractorFinance({
        contracts,
        planfactOperations,
      }),
    [contracts, planfactOperations],
  );

  const { totalAmount: familyTotalAmount, paymentPaid, paymentRemainder, paymentPercent } =
    contractorFinance;

  const contractorReceiptsData = useMemo(
    () =>
      buildContractReceiptsTabData({
        familyContracts: sortedContracts,
        planfactOperations,
        bankAccounts,
        bankAccountDrafts: Object.fromEntries(
          sortedContracts.map((ct) => [ct.id, ct.bank_account_id ?? null]),
        ),
        displayStatus: paymentRemainder <= 0 ? 'архив' : '—',
        familyFallbackPaid: paymentPaid,
      }),
    [sortedContracts, planfactOperations, bankAccounts, paymentRemainder, paymentPaid],
  );

  const contractorPaymentNotes = useMemo(() => {
    const notes: ContractNote[] = [];
    for (const ct of contracts) {
      for (const note of ct.payment_notes ?? []) {
        notes.push(note);
      }
    }
    return notes;
  }, [contracts]);

  const contractNumberById = useMemo(() => {
    const map: Record<number, string> = {};
    for (const ct of contracts) {
      map[ct.id] = ct.contract_number;
    }
    return map;
  }, [contracts]);

  const closePaymentNotes = useCallback((): void => {
    setPaymentNotesOpen(false);
  }, []);

  useEffect(() => {
    if (!paymentNotesOpen) return undefined;
    const onKey = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') {
        closePaymentNotes();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [paymentNotesOpen, closePaymentNotes]);

  useEffect(() => {
    if (activeTab !== 'receipts') {
      setPaymentNotesOpen(false);
    }
  }, [activeTab]);

  const handlePaymentNoteAdded = useCallback((note: ContractNote): void => {
    const appendNote = (notes: ContractNote[] | undefined): ContractNote[] => {
      const list = notes ?? [];
      if (list.some((row) => row.id === note.id)) return list;
      return [note, ...list];
    };
    setContracts((prev) =>
      prev.map((row) =>
        row.id === note.contract_id
          ? { ...row, payment_notes: appendNote(row.payment_notes) }
          : row,
      ),
    );
  }, []);

  const activeContractorType =
    cardEditing && cardDraft ? cardDraft.contractor_type : (contractor?.contractor_type ?? 'ЮЛ');
  const isLegal = activeContractorType === 'ЮЛ';

  if (!canOpenContractorCard) {
    return (
      <div className="data-page">
        <div className="empty-state">
          Нет доступа к карточке контрагента (нужна роль ОП_Менеджер / ОУП_Рук. проекта или отдел ALL_COMP).
        </div>
      </div>
    );
  }

  if (!canContractors) {
    return (
      <div className="data-page">
        <div className="empty-state">Нет доступа к модулю контрагентов (требуется право VIEW).</div>
      </div>
    );
  }

  if (Number.isNaN(contractorId)) {
    return (
      <div className="data-page">
        <div className="error-message">Некорректный идентификатор контрагента</div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="data-page">
        <div className="loading">Загрузка...</div>
      </div>
    );
  }

  if (error || !contractor) {
    return (
      <div className="data-page contractor-detail">
        <div className="error-message">{error || 'Контрагент не найден'}</div>
      </div>
    );
  }

  return (
    <div className="contractor-detail">
      <header className="contractor-detail__sticky">
        <div className="contractor-detail__header-bar">
          <div className="contractor-detail__title-row">
            {canEditContractor ? (
              <input
                type="text"
                className="contractor-detail__name-field"
                value={headerName}
                onChange={(e) => {
                  setHeaderName(e.target.value);
                  setNameError('');
                }}
                onBlur={() => void handleNameBlur()}
                disabled={nameSaving}
                aria-label="Название контрагента"
              />
            ) : (
              <h1>{contractor.name}</h1>
            )}
            {nameSaving ? (
              <span className="contractor-detail__inline-hint" aria-live="polite">
                Сохранение…
              </span>
            ) : null}
          </div>
          {canDeleteContractor ? (
            <button
              type="button"
              className="contractor-detail__delete-btn"
              onClick={handleDeleteClick}
            >
              Удалить
            </button>
          ) : null}
        </div>
        {nameError ? <div className="contractor-detail__name-error">{nameError}</div> : null}

        <div className="task-detail__stats contractor-detail__stats">
          <div className="task-detail__stat contract-detail__stat--hierarchy">
            <div className="task-detail__stat-title">Иерархия договоров</div>
            <div className="task-detail__stat-body">
              {!canContracts || sortedContracts.length === 0 ? (
                <div className="contract-detail__contractor-value">
                  <span className="contract-detail__contractor-value-text">—</span>
                </div>
              ) : (
                <>
                  <ul className="contract-detail__family-list">
                    {sortedContracts.map((ct) => {
                      const isLevel2 = ct.hierarchy_level === 2;
                      return (
                        <li key={ct.id} className="contract-detail__family-item">
                          <span
                            className={`contract-detail__family-sign${ct.has_original ? ' contract-detail__family-sign--ok' : ' contract-detail__family-sign--no'}`}
                            aria-hidden
                          >
                            {ct.has_original ? '✓' : '✗'}
                          </span>
                          {isLevel2 ? (
                            <span className="contract-detail__family-tree-mark" aria-hidden>
                              └──{' '}
                            </span>
                          ) : null}
                          <button
                            type="button"
                            className="task-detail__link contract-detail__family-num"
                            onClick={() => navigate(`/contracts/${ct.id}`)}
                          >
                            {ct.contract_number}
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                  <div className="task-detail__stat-meta contract-detail__family-legend">
                    <div>
                      <span className="contract-detail__family-sign contract-detail__family-sign--ok" aria-hidden>
                        ✓
                      </span>{' '}
                      подписан
                    </div>
                    <div>
                      <span className="contract-detail__family-sign contract-detail__family-sign--no" aria-hidden>
                        ✗
                      </span>{' '}
                      не подписан
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>

          {canReceipts ? (
            <div className="task-detail__stat">
              <div className="task-detail__stat-title">Остаток по поступлениям</div>
              <div className="task-detail__stat-body">
                <div className="task-detail__stat-row">
                  <span className="task-detail__stat-value">
                    {canContracts ? formatContractSumDisplay(paymentRemainder) : '—'}
                  </span>
                  {canContracts ? <PaymentDonut percent={paymentPercent} tone="success" /> : null}
                </div>
                <div className="task-detail__stat-meta">
                  <div>всего: {canContracts ? formatContractSumDisplay(familyTotalAmount) : '—'}</div>
                  <div>поступило: {canContracts ? formatContractSumDisplay(paymentPaid) : '—'}</div>
                </div>
              </div>
            </div>
          ) : null}
        </div>
      </header>

      <div className="contractor-detail__grid">
        <section className="contractor-detail__card">
          <div className="contractor-detail__card-head">
            <h2 className="contractor-detail__card-title">Карточка контрагента</h2>
            {canEditContractor ? (
              cardEditing ? (
                <div className="contractor-detail__card-actions">
                  <button
                    type="button"
                    className="submit-button"
                    disabled={cardSaving}
                    onClick={() => void handleSaveCardEdit()}
                  >
                    {cardSaving ? 'Сохранение…' : 'Сохранить'}
                  </button>
                  <button
                    type="button"
                    className="secondary-button"
                    disabled={cardSaving}
                    onClick={handleCancelCardEdit}
                  >
                    Отмена
                  </button>
                </div>
              ) : (
                <button type="button" className="secondary-button" onClick={handleStartCardEdit}>
                  Редактировать
                </button>
              )
            ) : null}
          </div>
          {cardError ? <div className="contractor-detail__card-error">{cardError}</div> : null}
          <div className="contractor-detail__tabs" role="tablist">
            <button
              type="button"
              role="tab"
              aria-selected={activeTab === 'requisites'}
              className={`contractor-detail__tab${activeTab === 'requisites' ? ' contractor-detail__tab--active' : ''}`}
              onClick={() => setActiveTabSafe('requisites')}
            >
              Реквизиты
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={activeTab === 'contacts'}
              className={`contractor-detail__tab${activeTab === 'contacts' ? ' contractor-detail__tab--active' : ''}`}
              onClick={() => setActiveTabSafe('contacts')}
            >
              Контакты
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={activeTab === 'hierarchy'}
              disabled={cardEditing}
              title={cardEditing ? 'Сначала сохраните или отмените редактирование реквизитов и контактов' : undefined}
              className={`contractor-detail__tab${activeTab === 'hierarchy' ? ' contractor-detail__tab--active' : ''}${cardEditing ? ' contractor-detail__tab--disabled' : ''}`}
              onClick={() => setActiveTabSafe('hierarchy')}
            >
              Иерархия
            </button>
            {canReceipts ? (
              <button
                type="button"
                role="tab"
                aria-selected={activeTab === 'receipts'}
                disabled={cardEditing}
                title={cardEditing ? 'Сначала сохраните или отмените редактирование реквизитов и контактов' : undefined}
                className={`contractor-detail__tab${activeTab === 'receipts' ? ' contractor-detail__tab--active' : ''}${cardEditing ? ' contractor-detail__tab--disabled' : ''}`}
                onClick={() => setActiveTabSafe('receipts')}
              >
                Поступления
              </button>
            ) : null}
          </div>

          {activeTab === 'requisites' && (
            <div className="contractor-detail__tab-panel" role="tabpanel">
              {cardEditing && cardDraft ? (
                <dl className="contractor-detail__dl">
                  <dt>ФЛ/ЮЛ</dt>
                  <dd>
                    <select
                      className="contractor-detail__field"
                      value={cardDraft.contractor_type}
                      onChange={(e) =>
                        setCardDraft({
                          ...cardDraft,
                          contractor_type: e.target.value as ContractorTypeCode,
                        })
                      }
                    >
                      <option value="ЮЛ">Юридическое лицо</option>
                      <option value="ФЛ">Физическое лицо</option>
                    </select>
                  </dd>
                  {isLegal ? (
                    <>
                      <dt>ИНН</dt>
                      <dd>
                        <input
                          type="text"
                          className="contractor-detail__field"
                          value={cardDraft.inn}
                          onChange={(e) => setCardDraft({ ...cardDraft, inn: e.target.value })}
                          maxLength={12}
                          required={isLegal}
                          aria-required={isLegal}
                        />
                      </dd>
                      <dt>Юридический адрес</dt>
                      <dd>
                        <textarea
                          className="contractor-detail__field contractor-detail__field--textarea"
                          value={cardDraft.address}
                          onChange={(e) => setCardDraft({ ...cardDraft, address: e.target.value })}
                          rows={3}
                        />
                      </dd>
                    </>
                  ) : (
                    <>
                      <dt>Паспортные данные</dt>
                      <dd>
                        <textarea
                          className="contractor-detail__field contractor-detail__field--textarea"
                          value={cardDraft.passport_data}
                          onChange={(e) => setCardDraft({ ...cardDraft, passport_data: e.target.value })}
                          rows={4}
                        />
                      </dd>
                    </>
                  )}
                </dl>
              ) : (
                <dl className="contractor-detail__dl">
                  <dt>ФЛ/ЮЛ</dt>
                  <dd>{contractorTypeLabel(contractor.contractor_type)}</dd>
                  {isLegal ? (
                    <>
                      <dt>ИНН</dt>
                      <dd>{dash(contractor.inn)}</dd>
                      <dt>Юридический адрес</dt>
                      <dd>{dash(contractor.address)}</dd>
                    </>
                  ) : (
                    <>
                      <dt>Паспортные данные</dt>
                      <dd>{dash(contractor.passport_data)}</dd>
                    </>
                  )}
                </dl>
              )}
            </div>
          )}

          {activeTab === 'contacts' && (
            <div className="contractor-detail__tab-panel" role="tabpanel">
              {cardEditing && cardDraft ? (
                <dl className="contractor-detail__dl">
                  <dt>ФИО для связи</dt>
                  <dd>
                    <input
                      type="text"
                      className="contractor-detail__field"
                      value={cardDraft.contact_full_name}
                      onChange={(e) => setCardDraft({ ...cardDraft, contact_full_name: e.target.value })}
                    />
                  </dd>
                  <dt>Должность/Отношение к объекту</dt>
                  <dd>
                    <input
                      type="text"
                      className="contractor-detail__field"
                      value={cardDraft.position_relation}
                      onChange={(e) => setCardDraft({ ...cardDraft, position_relation: e.target.value })}
                    />
                  </dd>
                  <dt>Телефон для связи</dt>
                  <dd>
                    <input
                      type="tel"
                      className="contractor-detail__field"
                      value={cardDraft.phone}
                      onChange={(e) => setCardDraft({ ...cardDraft, phone: e.target.value })}
                    />
                  </dd>
                  <dt>Email</dt>
                  <dd>
                    <input
                      type="email"
                      className="contractor-detail__field"
                      value={cardDraft.email}
                      onChange={(e) => setCardDraft({ ...cardDraft, email: e.target.value })}
                    />
                  </dd>
                  <dt>Дополнительные контакты</dt>
                  <dd>
                    <textarea
                      className="contractor-detail__field contractor-detail__field--textarea"
                      value={cardDraft.additional_contacts}
                      onChange={(e) => setCardDraft({ ...cardDraft, additional_contacts: e.target.value })}
                      rows={3}
                    />
                  </dd>
                </dl>
              ) : (
                <dl className="contractor-detail__dl">
                  <dt>ФИО для связи</dt>
                  <dd>{dash(contractor.contact_full_name)}</dd>
                  <dt>Должность/Отношение к объекту</dt>
                  <dd>{dash(contractor.position_relation)}</dd>
                  <dt>Телефон для связи</dt>
                  <dd>{dash(contractor.phone)}</dd>
                  <dt>Email</dt>
                  <dd>{dash(contractor.email)}</dd>
                  <dt>Дополнительные контакты</dt>
                  <dd>{dash(contractor.additional_contacts)}</dd>
                </dl>
              )}
            </div>
          )}

          {activeTab === 'hierarchy' && (
            <div className="contractor-detail__tab-panel" role="tabpanel">
              {!canContracts ? (
                <p className="contractor-detail__hint" style={{ padding: 0, background: 'none', border: 'none' }}>
                  Для списка договоров нужен доступ к модулю «Договоры» (право VIEW).
                </p>
              ) : sortedContracts.length === 0 ? (
                <p className="contractor-detail__muted">У этого контрагента пока нет договоров в доступной вам выборке.</p>
              ) : (
                <div className="task-detail__accordion task-detail__accordion--hierarchy contract-detail__subject-hierarchy">
                  <table className="contract-detail__general-table">
                    <tbody>
                      {sortedContracts.map((ct) => {
                        const contractExpanded = hierarchyExpandedContractIds.has(ct.id);
                        const contractTasks = canTasks
                          ? (tasksByContractId.get(ct.id) ?? []).sort((a, b) =>
                              workTypeLabel(a.work_type_id).localeCompare(
                                workTypeLabel(b.work_type_id),
                                'ru',
                                { sensitivity: 'base' },
                              ),
                            )
                          : [];
                        return (
                          <React.Fragment key={ct.id}>
                            <tr
                              className={`contract-row${canTasks ? '' : ' contract-detail__subject-hierarchy-row--static'}`}
                              onClick={canTasks ? () => toggleHierarchyContract(ct.id) : undefined}
                            >
                              <td className="contract-detail__general-table-label">
                                {canTasks ? (
                                  <>
                                    <span className="task-detail__chevron" aria-hidden>
                                      {contractExpanded ? '▼' : '▶'}
                                    </span>{' '}
                                  </>
                                ) : null}
                                {ct.hierarchy_level === 2 ? (
                                  <span className="contract-detail__family-tree-mark" aria-hidden>
                                    └──{' '}
                                  </span>
                                ) : null}
                                <button
                                  type="button"
                                  className="task-detail__link"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    navigate(`/contracts/${ct.id}`);
                                  }}
                                >
                                  <strong>{ct.contract_number}</strong>
                                </button>
                              </td>
                              <td className="contract-detail__general-table-value">
                                <div className="contract-detail__general-field contract-detail__general-field--readonly">
                                  {ct.contract_subject?.trim() ? ct.contract_subject : '—'}
                                </div>
                              </td>
                              <td className="contract-detail__general-table-original">
                                <span
                                  className={`contract-detail__family-sign${ct.has_original ? ' contract-detail__family-sign--ok' : ' contract-detail__family-sign--no'}`}
                                  title={ct.has_original ? 'Оригинал подписан' : 'Оригинал не подписан'}
                                  aria-label={ct.has_original ? 'Оригинал подписан' : 'Оригинал не подписан'}
                                >
                                  {ct.has_original ? '✓' : '✗'}
                                </span>
                              </td>
                            </tr>
                            {canTasks &&
                              contractExpanded &&
                              (contractTasks.length === 0 ? (
                                <tr className="task-row" key={`${ct.id}-empty`}>
                                  <td colSpan={3} className="task-detail__muted-text task-detail__muted-text--italic">
                                    Нет задач по договору
                                  </td>
                                </tr>
                              ) : (
                                contractTasks.map((tk) => (
                                  <tr
                                    key={tk.contracts_task_id}
                                    className="task-row"
                                    onClick={() => navigate(taskDetailPath(tk))}
                                    style={{ cursor: 'pointer' }}
                                  >
                                    <td colSpan={3}>
                                      {tk.task_id}: {workTypeLabel(tk.work_type_id)}
                                    </td>
                                  </tr>
                                ))
                              ))}
                          </React.Fragment>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {canReceipts && activeTab === 'receipts' && (
            <div className="contractor-detail__tab-panel" role="tabpanel">
              {!canContracts ? (
                <p className="contractor-detail__hint" style={{ padding: 0, background: 'none', border: 'none' }}>
                  Для данных о поступлениях нужен доступ к модулю «Договоры» (право VIEW).
                </p>
              ) : sortedContracts.length === 0 ? (
                <p className="contractor-detail__muted">У этого контрагента пока нет договоров в доступной вам выборке.</p>
              ) : (
                <>
                  <div
                    className={`contract-detail__deadlines-banner${
                      contractorReceiptsData.summaryKind === 'debt'
                        ? ' contract-detail__deadlines-banner--overdue'
                        : contractorReceiptsData.summaryKind === 'paid'
                          ? ' contract-detail__deadlines-banner--on-time'
                          : ' contract-detail__deadlines-banner--neutral'
                    }`}
                  >
                    <div className="contract-detail__deadlines-banner-main">
                      {contractorReceiptsData.summaryKind === 'paid' ? (
                        <div className="contract-detail__deadlines-banner-heading">
                          {contractorReceiptsData.summaryLabel}
                        </div>
                      ) : (
                        <>
                          <div
                            className={`contract-detail__deadlines-banner-heading${
                              contractorReceiptsData.summaryKind === 'debt'
                                ? ' contract-detail__deadlines-banner-heading--overdue'
                                : ''
                            }`}
                          >
                            {contractorReceiptsData.summaryLabel}
                          </div>
                          <div
                            className={`contract-detail__deadlines-banner-counters contract-detail__receipts-banner-counters${
                              contractorReceiptsData.summaryKind === 'debt'
                                ? ' contract-detail__deadlines-banner-counters--overdue'
                                : ''
                            }`}
                          >
                            <span className="contract-detail__receipts-banner-value">
                              {formatContractSumDisplay(contractorReceiptsData.summaryAmount ?? 0)}
                            </span>
                            <span className="contract-detail__deadlines-banner-unit">руб.</span>
                            <span className="contract-detail__receipts-banner-value">
                              {contractorReceiptsData.totals.paidPercent}%
                            </span>
                            <span className="contract-detail__deadlines-banner-unit">оплачено</span>
                          </div>
                        </>
                      )}
                    </div>
                    <div className="contract-detail__deadlines-banner-meta">
                      <span>Всего: {formatContractSumDisplay(contractorReceiptsData.totals.totalAmount)}</span>
                      <span>Поступило: {formatContractSumDisplay(contractorReceiptsData.totals.paidAmount)}</span>
                    </div>
                  </div>
                  <div className="task-detail__deadlines-layout contract-detail__receipts-layout">
                    <div className="contract-detail__receipts-main">
                    <table className="task-detail__mini-table task-detail__mini-table--compact contract-detail__receipts-table">
                      <thead>
                        <tr>
                          <th>Договор</th>
                          <th className="contract-detail__receipts-col-account">Счёт оплаты</th>
                          <th className="contract-detail__receipts-col-currency">Валюта</th>
                          <th className="contract-detail__receipts-col-num">Сумма</th>
                          <th className="contract-detail__receipts-col-num">Оплачено</th>
                          <th className="contract-detail__receipts-col-num">Остаток</th>
                          <th className="contract-detail__receipts-col-pct">%</th>
                        </tr>
                      </thead>
                      <tbody>
                        {contractorReceiptsData.tableRows.map((row) => (
                          <tr
                            key={row.key}
                            className={row.isTotal ? 'task-detail__deadlines-row--total' : undefined}
                          >
                            <td className="task-detail__td-text">
                              {row.isTotal ? (
                                <strong>{row.contractNumber}</strong>
                              ) : (
                                <>
                                  {row.hierarchyLevel === 2 ? (
                                    <span className="contract-detail__family-tree-mark" aria-hidden>
                                      └──{' '}
                                    </span>
                                  ) : null}
                                  <button
                                    type="button"
                                    className="task-detail__link"
                                    onClick={() => row.contractId != null && navigate(`/contracts/${row.contractId}`)}
                                  >
                                    <strong>{row.contractNumber}</strong>
                                  </button>
                                </>
                              )}
                            </td>
                            <td className="contract-detail__receipts-col-account">{row.planfactAccount}</td>
                            <td className="contract-detail__receipts-col-currency">{row.currencyType}</td>
                            <td className="contract-detail__receipts-col-num">
                              {row.isTotal ? (
                                <strong>{formatContractSumDisplay(row.totalAmount)}</strong>
                              ) : (
                                formatContractSumDisplay(row.totalAmount)
                              )}
                            </td>
                            <td className="contract-detail__receipts-col-num">
                              {formatContractSumDisplay(row.paidAmount)}
                            </td>
                            <td className="contract-detail__receipts-col-num">
                              {row.isTotal ? (
                                <strong>{formatContractSumDisplay(row.remainder)}</strong>
                              ) : (
                                formatContractSumDisplay(row.remainder)
                              )}
                            </td>
                            <td className="contract-detail__receipts-col-pct">
                              <PaymentDonut
                                percent={row.paidPercent}
                                tone="success"
                                className="contract-detail__receipts-donut"
                              />
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    </div>
                    <div className="contract-detail__receipts-side">
                      <button
                        type="button"
                        className="contract-detail__notes-open"
                        onClick={() => setPaymentNotesOpen(true)}
                      >
                        <span>Комментарии по оплатам</span>
                        {contractorPaymentNotes.length > 0 ? (
                          <span className="contract-detail__notes-open-count">
                            {contractorPaymentNotes.length}
                          </span>
                        ) : null}
                      </button>
                    </div>
                  </div>
                </>
              )}
            </div>
          )}
        </section>
      </div>

      {paymentNotesOpen ? (
        <div
          className="contract-detail__notes-drawer"
          role="dialog"
          aria-modal="true"
          aria-labelledby="contractor-detail-notes-drawer-title"
        >
          <button
            type="button"
            className="contract-detail__notes-drawer-backdrop"
            aria-label="Закрыть комментарии"
            onClick={closePaymentNotes}
          />
          <aside className="contract-detail__notes-drawer-panel">
            <div className="contract-detail__notes-drawer-head">
              <h2 id="contractor-detail-notes-drawer-title" className="contract-detail__notes-drawer-title">
                Комментарии по оплатам
              </h2>
              <button
                type="button"
                className="contract-detail__notes-drawer-close"
                aria-label="Закрыть"
                onClick={closePaymentNotes}
              >
                ×
              </button>
            </div>
            <ContractPaymentNotesChat
              variant="drawer"
              contractIds={level1ContractIds}
              notes={contractorPaymentNotes}
              canEdit={canEditContract && !cardEditing}
              onNoteAdded={handlePaymentNoteAdded}
              contractNumberById={contractNumberById}
            />
          </aside>
        </div>
      ) : null}

      {deleteDialogOpen ? (
        <div
          className="contractor-detail__modal-backdrop"
          role="presentation"
          onMouseDown={handleDeleteDialogBackdropMouseDown}
        >
          <div
            className="contractor-detail__modal"
            role="alertdialog"
            aria-modal="true"
            aria-labelledby="contractor-delete-title"
            onMouseDown={(e) => e.stopPropagation()}
          >
            <h3 id="contractor-delete-title" className="contractor-detail__modal-title">
              Удалить контрагента?
            </h3>
            <p className="contractor-detail__modal-text">
              Будет удалён контрагент «{contractor.name}». Действие нельзя отменить. Удаление возможно только если нет
              связанных договоров.
            </p>
            {deleteDialogError ? <div className="contractor-detail__modal-error">{deleteDialogError}</div> : null}
            <div className="contractor-detail__modal-actions">
              <button type="button" className="secondary-button" disabled={deleteBusy} onClick={handleDeleteDialogClose}>
                Отмена
              </button>
              <button type="button" className="contractor-detail__delete-btn" disabled={deleteBusy} onClick={() => void handleDeleteConfirm()}>
                {deleteBusy ? 'Удаление…' : 'Удалить'}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default ContractorDetailPage;
